import { Button } from '@mantine/core';
import { IconFileText, IconPaperclip } from '@tabler/icons-react';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDay } from '../../format.js';
import { CREW_FILE_ACCEPT, CrewFileViewer, crewFileErrorKey } from '../personnel/CrewFileViewer.js';
import { errorCode } from '../personnel/source.js';
import classes from './Purchasing.module.css';
import type { RfqSource } from './source.js';
import type { RfqDetail, RfqQuotation, RfqQuotationDocument } from './types.js';

/**
 * Teklif belgeleri (04.11 notları D1; ADR 0168): portala kayıtlı olmayan tedarikçinin kâğıt ya da
 * e-postayla gelen teklifini ofis elle girer ve tedarikçinin belgesini (PDF, JPG, PNG; en çok
 * 10 MB) ekler. Yalnız ofisin girdiği teklifler listelenir; portal teklifi tedarikçinin kendi
 * sürümüdür. Yeni belge eskisini silmez; belge uygulama içinde açılır, açılış kayda geçer.
 */
export function QuotationDocuments({
  detail,
  source,
  onChange,
}: {
  detail: RfqDetail;
  source: RfqSource;
  onChange: (next: RfqDetail) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.rfq.detail.documents.${key}`, options);
  const titleId = useId();
  const [open, setOpen] = useState<RfqQuotationDocument | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const office = detail.quotations.filter((q) => q.source !== 'portal');
  if (office.length === 0) return null;
  const canAttach = detail.can.attachDocuments === true && source.attachQuotationDocument;
  const url = source.quotationDocumentUrl;

  const attach = async (quotation: RfqQuotation, file: File | undefined) => {
    if (!file || !source.attachQuotationDocument) return;
    setBusy(quotation.id);
    setError(null);
    setNotice(null);
    try {
      onChange(await source.attachQuotationDocument(detail.id, quotation.id, file));
      setNotice(k('attached'));
    } catch (failure) {
      setError(t(crewFileErrorKey(errorCode(failure))));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section aria-labelledby={titleId}>
      <h3 id={titleId} className={classes.subTitle}>
        {k('title')}
      </h3>
      <p className={classes.note}>{k('note')}</p>
      {notice ? (
        <p className={classes.notice} data-tone="success" role="status">
          {notice}
        </p>
      ) : null}
      {error ? (
        <p className={classes.error} role="alert">
          {error}
        </p>
      ) : null}
      <ul className={classes.quoteDocs}>
        {office.map((quotation) => (
          <li key={quotation.id} className={classes.quoteDocRow}>
            <span className={classes.quoteDocSupplier}>{quotation.supplierName}</span>
            <div className={classes.quoteDocFiles}>
              {(quotation.documents ?? []).length === 0 ? (
                <span className={classes.muted}>{k('none')}</span>
              ) : (
                (quotation.documents ?? []).map((document) => (
                  <span key={document.id} className={classes.quoteDocFile}>
                    {url ? (
                      <Button
                        variant="subtle"
                        size="compact-sm"
                        leftSection={<IconFileText size={14} aria-hidden />}
                        aria-label={t('crewFiles.open', { name: document.name })}
                        onClick={() => setOpen(document)}
                      >
                        <span className={classes.awardDocName}>{document.name}</span>
                      </Button>
                    ) : (
                      <span className={classes.awardDocName}>{document.name}</span>
                    )}
                    <span className={classes.muted}>
                      {k('meta', {
                        date: formatDay(document.createdAt.slice(0, 10)),
                        name: document.createdByName ?? '—',
                      })}
                    </span>
                  </span>
                ))
              )}
            </div>
            {canAttach ? (
              <AttachButton
                label={k('attachFor', { supplier: quotation.supplierName })}
                text={k('attach')}
                busy={busy === quotation.id}
                onFile={(file) => void attach(quotation, file)}
              />
            ) : null}
          </li>
        ))}
      </ul>
      {url ? (
        <CrewFileViewer
          file={
            open
              ? {
                  id: open.id,
                  name: open.name,
                  size: open.size,
                  mimeType: open.mimeType,
                  createdAt: open.createdAt,
                }
              : null
          }
          url={url}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </section>
  );
}

function AttachButton({
  label,
  text,
  busy,
  onFile,
}: {
  label: string;
  text: string;
  busy: boolean;
  onFile: (file: File | undefined) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <span>
      <input
        ref={input}
        type="file"
        accept={CREW_FILE_ACCEPT}
        className={classes.fileInput}
        aria-label={label}
        tabIndex={-1}
        onChange={(event) => {
          onFile(event.currentTarget.files?.[0]);
          event.currentTarget.value = '';
        }}
      />
      <Button
        size="xs"
        variant="light"
        leftSection={<IconPaperclip size={14} aria-hidden />}
        loading={busy}
        title={label}
        onClick={() => input.current?.click()}
      >
        {text}
      </Button>
    </span>
  );
}
